import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Send, Search, LoaderCircle } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import WorkflowFlowEditor, {
  WorkflowStepDraft,
  draftsToInput,
  draftsFromPlan,
  findFolderName,
  workflowPlanHasIssues,
} from '../components/workflow-flow'

const NO_TEMPLATE_OPTION = '__none__'
const PREVIOUS_PLAN_OPTION = '__previous__'

interface LatestRevisionWorkflowPlan {
  steps: { index: number; name: string; durationDays: number | null; reviewerIds: string[] }[]
  targetFolderId: string | null
}

interface SubmitForReviewDialogProps {
  submittalId: string
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refetch: () => void
  /** Newest revision of the submittal (index.tsx's currentRevisionFull) —
   *  its workflowPlan (stored shape, see src/graphql/submittals/lifecycle.ts)
   *  drives the "plan from the previous revision" prefill below. */
  latestRevision?: { workflowPlan: LatestRevisionWorkflowPlan | null } | null
}

/**
 * Mounted only while open (see the `{open && <SubmitForReviewDialog .../>}`
 * guard in index.tsx — same conditional-mount convention SubmittalFieldsCard
 * uses for its edit dialog), so PROJECT_MEMBERS only fires when actually
 * needed and every open starts from empty selection/search/message state —
 * including the template select, which resets to its prefilled default
 * (previous plan if one exists, else no-template) on every open.
 *
 * Three modes, chosen by the top Select:
 * - no template (default when the previous revision has no plan): today's
 *   flat reviewer checkbox list, `{ steps: null, templateId: null }`.
 * - a workflow template: the shared WorkflowFlowEditor canvas, seeded from the
 *   template's steps, `{ steps: draftsToInput(steps), templateId }`.
 * - "plan from the previous revision" (synthetic option, shown only when
 *   one exists, preselected by default): the editor seeded from
 *   latestRevision.workflowPlan.steps, `{ steps: draftsToInput(steps),
 *   templateId: null }` — it's a copy, not a link back to a template.
 */
const SubmitForReviewDialog: React.FC<SubmitForReviewDialogProps> = ({
  submittalId,
  projectId,
  open,
  onOpenChange,
  refetch,
  latestRevision,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [message, setMessage] = useState('')

  const previousPlan = latestRevision?.workflowPlan ?? null

  const [templateOption, setTemplateOption] = useState<string>(() =>
    previousPlan ? PREVIOUS_PLAN_OPTION : NO_TEMPLATE_OPTION,
  )
  const [steps, setSteps] = useState<WorkflowStepDraft[]>(() =>
    previousPlan ? draftsFromPlan(previousPlan.steps) : [],
  )

  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })
  const members = (membersData?.project?.users ?? []).map((m: any) => m.user).filter(Boolean)

  const { data: templatesData } = useQuery(Query.SUBMITTAL_WORKFLOW_TEMPLATES, {
    variables: { projectId },
    skip: !projectId,
  })
  const templates: any[] = templatesData?.submittalWorkflowTemplates || []

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return members
    return members.filter(
      (m: any) => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q),
    )
  }, [members, search])

  const [submitForReview, { loading: submitting }] = useMutation(Mutation.SUBMIT_SUBMITTAL_FOR_REVIEW)

  const toggleReviewer = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const isStepsMode = templateOption !== NO_TEMPLATE_OPTION
  const isTemplateSelected = isStepsMode && templateOption !== PREVIOUS_PLAN_OPTION

  // The Publish node is display-only here (the target folder rides in with the
  // template / previous plan and the backend snapshots it server-side), but it
  // should still SHOW where an approved revision will land.
  const publishFolderId =
    templateOption === PREVIOUS_PLAN_OPTION
      ? previousPlan?.targetFolderId ?? null
      : isTemplateSelected
        ? templates.find((tpl) => tpl.id === templateOption)?.targetFolderId ?? null
        : null
  // Same query FolderPicker runs elsewhere — an Apollo cache hit in practice;
  // it only turns the stored folder id into a display name.
  const { data: foldersData } = useQuery(Query.DOC_FOLDER_TREE, {
    variables: { projectId },
    skip: !projectId || !publishFolderId,
  })
  const publishFolderName = findFolderName(foldersData?.docFolderTree, publishFolderId)

  const handleTemplateOptionChange = (value: string) => {
    setTemplateOption(value)
    if (value === NO_TEMPLATE_OPTION) {
      setSteps([])
      return
    }
    if (value === PREVIOUS_PLAN_OPTION) {
      setSteps(previousPlan ? draftsFromPlan(previousPlan.steps) : [])
      return
    }
    const template = templates.find((tpl) => tpl.id === value)
    setSteps(
      draftsFromPlan(
        (template?.steps || []).map((s: any) => ({
          name: s.name,
          durationDays: s.durationDays,
          reviewerIds: (s.reviewers || []).map((r: any) => r.id),
        })),
      ),
    )
  }

  const handleClose = () => {
    if (submitting) return
    onOpenChange(false)
  }

  const canSubmit = isStepsMode ? !workflowPlanHasIssues(steps) : selectedIds.length > 0

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      await submitForReview({
        variables: {
          id: submittalId,
          message: message.trim() || null,
          reviewerIds: isStepsMode ? [] : selectedIds,
          steps: isStepsMode ? draftsToInput(steps) : null,
          templateId: isTemplateSelected ? templateOption : null,
        },
      })
      toastr.success('', t('Submittals_submit_for_review_success'))
      refetch()
      onOpenChange(false)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className={cn('sm:max-w-lg', isStepsMode && 'sm:max-w-6xl')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            {t('Submittals_submit_for_review_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('Submittals_wf_template_label')}</Label>
            <Select value={templateOption} onValueChange={handleTemplateOptionChange}>
              <SelectTrigger className="h-8 w-full text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEMPLATE_OPTION}>{t('Submittals_wf_no_template')}</SelectItem>
                {previousPlan && (
                  <SelectItem value={PREVIOUS_PLAN_OPTION}>{t('Submittals_wf_from_previous')}</SelectItem>
                )}
                {templates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isStepsMode ? (
            <WorkflowFlowEditor
              steps={steps}
              onChange={setSteps}
              members={members}
              publishFolderName={publishFolderName}
            />
          ) : (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>{t('Submittals_submit_for_review_reviewers_label')}</Label>
                <Badge variant="secondary">{selectedIds.length}</Badge>
              </div>

              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('Submittals_submit_for_review_search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pl-8 text-sm"
                />
              </div>

              <div className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border p-1.5">
                {filteredMembers.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    {t('Submittals_submit_for_review_no_members')}
                  </p>
                ) : (
                  filteredMembers.map((member: any) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2.5 rounded px-2 py-1.5 hover:bg-muted"
                    >
                      <Checkbox
                        id={`submittal-reviewer-${member.id}`}
                        checked={selectedIds.includes(member.id)}
                        onCheckedChange={() => toggleReviewer(member.id)}
                      />
                      <Label
                        htmlFor={`submittal-reviewer-${member.id}`}
                        className="flex-1 cursor-pointer text-sm font-normal"
                      >
                        {member.name}
                        {member.email && <span className="ml-1.5 text-muted-foreground">{member.email}</span>}
                      </Label>
                    </div>
                  ))
                )}
              </div>

              {selectedIds.length === 0 && (
                <p className="mt-1.5 text-xs text-destructive">
                  {t('Submittals_submit_for_review_validation')}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="submittal-review-message">
              {t('Submittals_submit_for_review_message_label')}
            </Label>
            <Textarea
              id="submittal-review-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('Submittals_submit_for_review_message_placeholder')}
              rows={3}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting || !canSubmit}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Submittals_submit_for_review_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SubmitForReviewDialog
